import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, CheckCircle, Car, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const VehicleBox = ({ vehicle, onClear }) => (
  <div className="flex items-center gap-4 bg-[#F2F2F2] border-l-4 border-[#EFDF00] p-3" data-testid="callback-vehicle">
    {vehicle.image_url ? (
      <img src={vehicle.image_url} alt="" className="w-20 h-14 object-cover" />
    ) : (
      <div className="w-20 h-14 bg-white flex items-center justify-center"><Car className="w-6 h-6 text-[#888B8D]" /></div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-xs uppercase tracking-wide text-[#888B8D]">Ihr Wunschfahrzeug</p>
      <p className="font-bold text-black truncate" data-testid="callback-vehicle-name">{vehicle.brand} {vehicle.model}</p>
      <p className="text-sm text-[#888B8D]">
        {vehicle.year ? `Baujahr ${vehicle.year}` : ""}{vehicle.price ? ` · ${Number(vehicle.price).toLocaleString("de-DE")} €` : ""}
      </p>
    </div>
    <button type="button" onClick={onClear} className="p-2 text-[#888B8D] hover:text-black" aria-label="Fahrzeug entfernen" data-testid="callback-vehicle-clear">
      <X className="w-4 h-4" />
    </button>
  </div>
);

export const CallbackForm = ({ vehicle: initialVehicle = null, onVehicleCleared }) => {
  const [vehicle, setVehicle] = useState(initialVehicle);
  const prefill = initialVehicle ? `Ich interessiere mich für den ${initialVehicle.brand} ${initialVehicle.model}.` : "";
  const [form, setForm] = useState({ name: "", phone: "", email: "", website: "", message: prefill });
  const clearVehicle = () => {
    setVehicle(null);
    setForm((f) => ({ ...f, message: f.message === prefill ? "" : f.message }));
    onVehicleCleared?.();
  };
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Bitte Telefonnummer oder E-Mail angeben");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/callback`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        message: form.message.trim(),
        vehicle_id: vehicle?.id || "",
        website: form.website,
      });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bitte Eingaben prüfen");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white border-l-4 border-[#EFDF00] p-8 flex items-start gap-4" data-testid="callback-success">
        <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
        <div>
          <h3 className="text-xl font-bold text-black mb-1">Vielen Dank!</h3>
          <p className="text-[#888B8D]">Wir haben Ihre Angaben erhalten und melden uns schnellstmöglich bei Ihnen.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white p-8 shadow-sm space-y-5" data-testid="callback-form">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-[#EFDF00] flex items-center justify-center">
          <PhoneCall className="w-6 h-6 text-black" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-black">Wir melden uns bei Ihnen</h3>
          <p className="text-sm text-[#888B8D]">Name und Telefonnummer oder E-Mail hinterlassen.</p>
        </div>
      </div>
      {vehicle && <VehicleBox vehicle={vehicle} onClear={clearVehicle} />}
      <div>
        <Label htmlFor="cb-name">Name *</Label>
        <Input id="cb-name" required minLength={2} value={form.name} onChange={set("name")} data-testid="callback-name-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cb-phone">Telefon</Label>
          <Input id="cb-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="callback-phone-input" />
        </div>
        <div>
          <Label htmlFor="cb-email">E-Mail</Label>
          <Input id="cb-email" type="email" value={form.email} onChange={set("email")} data-testid="callback-email-input" />
        </div>
      </div>
      <div>
        <Label htmlFor="cb-message">Nachricht (optional)</Label>
        <Textarea id="cb-message" rows={3} maxLength={2000} value={form.message} onChange={set("message")}
          placeholder="z.B. Fragen zum Fahrzeug oder wann wir Sie am besten erreichen" data-testid="callback-message-input" />
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="hidden" />
      <Button type="submit" disabled={sending} className="w-full bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold py-6" data-testid="callback-submit-btn">
        {sending ? "Wird gesendet ..." : "Rückruf anfordern"}
      </Button>
    </form>
  );
};

export const CallbackPage = () => {
  const vehicle = useLocation().state?.vehicle || null;
  const [hasVehicle, setHasVehicle] = useState(!!vehicle);
  return (
    <div className="min-h-screen bg-[#F2F2F2]" data-testid="callback-page">
      <Header />
      <section className="py-16">
        <div className="max-w-xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-black mb-3">{hasVehicle ? "Fahrzeug anfragen" : "Kontakt"}</h1>
          <p className="text-[#888B8D] mb-8">Hinterlassen Sie uns Ihre Daten - wir rufen Sie zurück oder schreiben Ihnen.</p>
          <CallbackForm vehicle={vehicle} onVehicleCleared={() => setHasVehicle(false)} />
        </div>
      </section>
      <Footer />
    </div>
  );
};
